import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { parse } from 'query-string';
import classnames from 'classnames';
import { Form, FormGroupInputControl, connectedComponent, clearExceptions } from 'gwx-voyager-components';
import { sendClientInvitation, loadClientInvitations } from '../clientInvitations.actions';

/**
 * ClientInvitationForm component provides a form used
 * to send a new Invitation for a desired Community.
 *
 * @class ClientInvitationForm
 * @extends {Component}
 */
class ClientInvitationForm extends Component {
	constructor(props) {
		super(props);
		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleSelectChange = this.handleSelectChange.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.fetchCreatedInvitation = this.fetchCreatedInvitation.bind(this);
		this.retryFetchInvitation = this.retryFetchInvitation.bind(this);
		this.handleCheck = this.handleCheck.bind(this);
		this.inputDisplayNames = {
			communityId: 'Community',
			firstName: 'First Name',
			lastName: 'Last Name',
			email: 'Email',
		};
		this.checkBoxes = {
			isAdminInvitationAllowed:'Invite Other Admins'
		};
		this.requiredFields = ['Community', 'First Name', 'Last Name', 'Email'];
		this.state = {
			buttonsDisabled: false,
			email: '',
			firstName: '',
			lastName: '',
			communityId: props.communityId,
			communitySelector: null,
			isAdminInvitationAllowed: false
		};
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if ((Object.keys(this.props.exceptions).length === 0
			&& Object.keys(nextProps.exceptions).length !== 0)
			|| this.props.exceptions.timeStamp !== nextProps.exceptions.timeStamp) {
			this.setState({ buttonsDisabled: false });
		}
	}

	componentWillUnmount() {
		this.props.clearExceptions && this.props.clearExceptions();
	}

	/**
	 * Handles changing the value of an input.
	 *
	 * @param {Event} e
	 * 		The input change event.
	 */
	handleInputChange(e) {
		const { target } = e;
		this.setState({ [target.name]: target.value });
	}

	/**
	 * Handles changing the value of the Selector input.
	 *
	 * @param {Event} e
	 * 		The input change event.
	 */
	handleSelectChange(e) {
		this.setState({ communityId: e ? e.value : '', communitySelector: e });
	}

	handleCheck(e, c) {
		this.setState((prevState) => ({
			 [c]: !prevState[c]
		}));
	}

	/**
	 * Handles submitting the Client Invitation form.
	 *
	 * @param {Event} e
	 * 		The form submission event.
	 */
	handleSubmit(e) {
		e.preventDefault();
		let clientInvitation = Object.keys(this.inputDisplayNames).reduce((acc, input) => {
			acc[input] = this.state[input].trim();
			return acc;
		}, {});
		clientInvitation = { ...clientInvitation, isAdminInvitationAllowed: this.state.isAdminInvitationAllowed };
		this.setState({ buttonsDisabled: true }, () => this.props.send(clientInvitation, this.props.query)
			.then((response) => {
				const exception = response.error;
				if (!exception) {
					window.newInvitationId = response.response.result.id;
					this.fetchCreatedInvitation();
				}
				this.props.discard(this.props.communityId, response);
			}));
	}

	fetchCreatedInvitation() {
		window.intervalId = setInterval(this.retryFetchInvitation, 3000);
		setTimeout(() => clearInterval(window.intervalId), 10000);
	}

	retryFetchInvitation() {
		//this.props.query = {};
		this.props.fetchClientInvitations({...this.props.query,communityId:this.props.communityId}).then((fetchResponse) => {
			if (fetchResponse && fetchResponse.type === 'CLIENT_INVITATIONS_SUCCESS') {
				const { clientInvitations } = fetchResponse.response.entities;
				Object.keys(clientInvitations).forEach((invId) => {
					if (window.newInvitationId === invId) {
						clearInterval(window.intervalId);
					}
				});
			}
		});
	}

	render() {
		const inputArr = Object.keys(this.inputDisplayNames).map((input) => {
			const inError = !!(this.props.exceptions.fields && this.props.exceptions.fields[input]);
			return (
				<FormGroupInputControl
					control={({
						id: input,
						name: input,
						onChange: this.handleInputChange,
						value: input === 'communityId' ? this.props.communityName : this.state[input],
						inError,
						disabled: input === 'communityId' ? true : false,
					})}
					helpText={inError ? this.props.exceptions.fields[input] : null}
					key={input}
					label={this.inputDisplayNames[input]}
					isRequired={this.requiredFields.includes(this.inputDisplayNames[input]) ? true : false}
				/>
			);
		});

		const checkBoxes = Object.keys(this.checkBoxes).map((c) => (
			<div className='form-check'>
				<input
					name={`option-${c}`}
					type='checkbox'
					className={classnames('form-check-input')}
					value={c}
					onChange={(e) => this.handleCheck(e, c)}
					checked={this.state[c]}
				/>
				<label className='form-check-label' htmlFor='isAdminInvitationAllowed'>
					{this.checkBoxes[c]}
				</label>
			</div>
		));
		
		const formProps = {
			buttonsDisabled: this.state.buttonsDisabled,
			legend: 'Create Invitation',
			fieldRequiredText: this.requiredFields !== undefined && this.requiredFields.length > 0,
			onClickDiscard: () => this.props.discard(this.props.communityId),
			onSubmit: this.handleSubmit,
			primaryButtonLabel: 'Send'
		};
		return (
			<div className='container-fluid'>
				<Form {...formProps}>
					{inputArr}
					{checkBoxes}
				</Form>
			</div>
		);
	}
}
ClientInvitationForm.propTypes = {
	/**
	 * Function for clearing the exceptions from the
	 * store.
	 */
	clearExceptions: PropTypes.func.isRequired,
	discard: PropTypes.func.isRequired,
	exceptions: PropTypes.shape({
		fields: PropTypes.object,
		timeStamp: PropTypes.number
	}).isRequired,
	send: PropTypes.func.isRequired,
	query: PropTypes.shape({
		resultsPerPage: PropTypes.string,
		page: PropTypes.string
	}).isRequired
};

/**
 * Maps the current state of the Redux store to a set of
 * properties to be passed to the component.
 *
 * @param {object} state
 */
const mapStateToProps = (state, props) => {
	const {
		match: { params: { id: communityId } },
	} = props;

	const {
		exceptions,
		entities: {
			community,
		}
	} = state;

	const { name: communityName } = community[communityId];
	return {
		communityId,
		exceptions,
		communityName
	}
}
	
/**
 * Creates a set of functions that utilize the dispatcher to
 * dispatch actions. These are the Action Creators for the
 * InvitationsForm component.
 *
 * @param {function} dispatch
 * 		The redux dispatcher.
 * @param {object} props
 * 		The props being passed to the component.
 */
const mapDispatchToProps = (dispatch, props) => ({
	query: { size: '25', page: '0', ...parse(props.location.search) },
	clearExceptions: () => { dispatch(clearExceptions); },
	/**
	 * Discards the form.
	 */
	discard: (communityId, response) => {
		if (!response || !response.error) {
			dispatch(clearExceptions);
			props.history.push(`/${communityId}/clientInvitations`);
		}
	},
	/**
	 * Makes the fetch request to send the given Client Invitation.
	 *
	 * @param {object} invitation
	 * 		The Client Invitation to be sent.
	 */
	send: (invitation, query) => dispatch(sendClientInvitation(null, invitation, query)),
	/**
	 * Makes the fetch request for the list of Client Invitations.
	 */
	fetchClientInvitations: (query) => dispatch(loadClientInvitations(query))
});

const ClientInvitationFormContainer =	connectedComponent(ClientInvitationForm, mapStateToProps, mapDispatchToProps);

export {
	ClientInvitationFormContainer as default,
	mapDispatchToProps,
	ClientInvitationForm
};
